import { formatDate } from '@angular/common';
import { Injectable, Inject, inject, signal, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { ITokenService, DA_SERVICE_TOKEN } from '@delon/auth';
import { _HttpClient, MenuService } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService, NzModalRef } from 'ng-zorro-antd/modal';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { readJson } from 'src/app/core/model-helper';

import { ConcurrentUsersComponent } from '../components/concurrent-user/concurrent-user.component';
import { LicenseInfoComponent } from '../components/license-info/license-info.component';
import { NotificationService } from './notification.service';
import { AppService } from './app.service';

@Injectable()
export class LicenseService implements OnDestroy {
  private routerSubscription?: Subscription;

  constructor(
    private http: _HttpClient,
    private reuseTabSrv: ReuseTabService,
    private modalService: NzModalService,
    private msg: NzMessageService,
    @Inject(DA_SERVICE_TOKEN) private tokenService: ITokenService,
    private menuService: MenuService,
    private router: Router
  ) {
    // Close License Reminder (and any other modals) when user is redirected to login page
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        if (e.urlAfterRedirects?.includes('passport/login') || e.url?.includes('passport/login')) {
          this.modalService.closeAll();
        }
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  get loading() {
    return this.http.loading;
  }

  appSrv = inject(AppService);
  notificationSrv = inject(NotificationService);
  licenseInfo: License = new License();
  inGracePeriod: boolean = false;
  terminals: any[] = [];
  enforceTwoFactorAuthentication: boolean = false;
  userTwoFactorEnabled: boolean = false;

  modalOpen: any = {
    licenseInfo: false,
    concurrentUsers: false
  };

  setLicense(info: any) {
    this.licenseInfo = new License();
    this.licenseInfo.fromJson(info);
  }

  refreshLicense() {
    return new Promise((resolve, reject) => {
      this.http.get<CheckLicenseResult>('api/license/mylicense').subscribe({
        next: result => {
          this.inGracePeriod = result.inGracePeriod;
          this.terminals = result.terminals;
          this.enforceTwoFactorAuthentication =
            result.enforceTwoFactorAuthentication;
          this.userTwoFactorEnabled = result.userTwoFactorEnabled;

          resolve(true);
        },
        error: () => {
          reject();
        }
      });
    });
  }

  checkLicense() {
    // const terminalId = this.appSrv.getTerminalId();
    this.http
      .get<CheckLicenseResult>('api/license/mylicense', {
        // terminalId: terminalId
      })
      .subscribe(result => {
        this.setLicense(result.license);
        this.inGracePeriod = result.inGracePeriod;
        this.terminals = result.terminals;
        this.enforceTwoFactorAuthentication =
          result.enforceTwoFactorAuthentication;
        this.userTwoFactorEnabled = result.userTwoFactorEnabled;

        // Check if MFA is enforced but user hasn't enabled it
        if (
          result.enforceTwoFactorAuthentication &&
          !result.userTwoFactorEnabled
        ) {
          this.show2FAEnforcementModal();
          return;
        }

        // // 0: No action, 1: Renew, 2: Choose Plan, 3: Renewal Reminder
        // if (result.action === 0 || result.action === 3) {
        //   // If license is still valid, register the terminal
        //   this.registerTerminal();
        // }

        if (result.action) {
          this.showLicenseReminder(result);
        }
      });
  }

  cancel() {
    // window.location.href = '/#/account/subscription';
  }

  private showLicenseReminder(result: CheckLicenseResult): void {
    let msg = result.message;
    const action = result.action;

    if (action === 1) {
      // update license
    } else if (action === 2) {
      msg = result.message;
    }

    const tid = this.tokenService.get()!['tenantId'];
    const clearTabs = this.reuseTabSrv.clear.bind(this.reuseTabSrv);
    let buttons: any[] = [
      {
        label: 'Renew Subscription',
        type: 'primary',
        show: action === 1 || action === 3,
        onClick: () => {
          modal.destroy();
          window.location.href = `/#/account/subscription/subscribe/${tid}?mode=normal&type=purchase`;
          setTimeout(() => {
            clearTabs();
          }, 100);
        }
      },
      {
        label: 'Choose Plan',
        type: 'dashed',
        danger: true,
        show: action === 2,
        onClick: () => {
          modal.destroy();
          window.location.href = `/#/account/subscription?tid=${tid}`;
          setTimeout(() => {
            clearTabs();
          }, 100);
        }
      },
      {
        label: 'Connected Users',
        type: 'default',
        onClick: () => {
          // modal.destroy();
          this.showConcurrentUsers();
        }
      },
      {
        label: 'Switch Company',
        type: 'danger',
        onClick: () => {
          modal.destroy();
          window.location.href = '/#/account/companies';
          setTimeout(() => {
            clearTabs();
          }, 100);
        }
      },
      {
        label: !result.locked ? 'Maybe Later' : 'Signout',
        type: 'dashed',
        onClick: () => {
          modal.destroy();
          if (result.locked) {
            this.tokenService.clear();
            window.location.href = '/#/passport/login';
          }
        }
      }
    ];

    if (result.inGracePeriod) {
      buttons = [
        {
          label: '5 Days grace period',
          type: 'default',
          onClick: () => {
            this.http.post('api/license/applyGracePeriod').subscribe({
              next: data => {
                this.msg.info(
                  `Grace period applied until ${formatDate(
                    data.gracePeriod,
                    'MMM dd, yyyy',
                    'en-US'
                  )}`
                );
                modal.destroy();
              },
              error: () => {
                modal.destroy();
              }
            });
          }
        },
        ...buttons
      ];
    }

    let title = 'License Reminder';

    if (
      result.exceedNumberOfStandardUsers ||
      result.exceedNumberOfAccountantUsers
    )
      title = 'User Limit Exceeded';

    // license locked
    if (result.action === 4) {
      this.menuService.clear();
    }

    const modal: NzModalRef = this.modalService.create({
      nzTitle: title,
      nzClosable: false,
      nzMaskClosable: false,
      nzContent: msg,
      nzWidth: '600px',
      nzOnCancel: this.cancel,
      nzFooter: buttons
    });
  }

  showConcurrentUsers() {
    const ref = this.modalService.create({
      nzTitle: 'Concurrent Users',
      nzContent: ConcurrentUsersComponent,
      nzWidth: '750px',
      nzOnCancel: () => {
        this.modalOpen.concurrentUsers = false;
        ref.destroy();
      },
      nzFooter: null
    });
  }

  show(tenantId: string, ownerId: string) {
    const ref = this.modalService.create({
      nzTitle: 'License Info',
      nzContent: LicenseInfoComponent,
      nzWidth: '750px',
      nzOnCancel: () => {
        this.modalOpen.licenseInfo = false;
        ref.destroy();
      },
      nzFooter: [
        {
          type: 'primary',
          label: 'Ok',
          onClick: () => {
            ref.destroy();
          }
        }
      ]
    });
    const instance = ref.getContentComponent();
    instance.tenantId = tenantId;
    instance.ownerId = ownerId;
  }

  private show2FAEnforcementModal(): void {
    const clearTabs = this.reuseTabSrv.clear.bind(this.reuseTabSrv);

    const modal = this.modalService.create({
      nzTitle: 'Multi-Factor Authentication Required',
      nzClosable: false,
      nzMaskClosable: false,
      nzContent:
        'This company requires multi-factor authentication to be enabled for all users. Please enable MFA in your account security settings to continue accessing this company.',
      nzWidth: '600px',
      nzFooter: [
        {
          label: 'Enable MFA',
          type: 'primary',
          onClick: () => {
            modal.destroy();
            window.location.href =
              '/#/account/settings/security?enable2FA=true';
            setTimeout(() => {
              clearTabs();
            }, 100);
          }
        },
        {
          label: 'Switch Company',
          type: 'default',
          onClick: () => {
            modal.destroy();
            window.location.href = '/#/account/companies';
            setTimeout(() => {
              clearTabs();
            }, 100);
          }
        },
        {
          label: 'Sign Out',
          type: 'default',
          onClick: () => {
            modal.destroy();
            this.tokenService.clear();
            window.location.href = '/#/passport/login';
          }
        }
      ]
    });
  }

  // public registerTerminal() {
  //   const terminalId = this.appSrv.getTerminalId();
  //   // Get browser name
  //   const terminalName = this.appSrv.getTerminalName();
  //   this.notificationSrv.registerTerminal(terminalId, terminalName);
  // }
}

export class License {
  constructor() {}

  accountant: any = {};
  dealer: any = {};
  buyer: any = {};
  productKey: string = '';
  productName = '';
  plan = '';

  expiryDate: Date | null = null;

  numberOfCompanies = 1;
  numberOfUsers = 1;
  concurrentUsers = 1;
  concurrentUsersOccupied = 1;
  nouOccupied = 1;
  numberOfNamedUsers = 1;
  nocOccupied = 1;
  modules: any[] = [];

  get expired() {
    return this.expiryDate && new Date() > this.expiryDate;
  }

  fromJson(json: string) {
    readJson(this, json);
  }
}

export class CheckLicenseResult {
  public expired: boolean = false;
  public locked: boolean = false;
  public inGracePeriod: boolean = false;
  public action: number = 0;
  public message: string = '';
  public license: License = new License();
  public exceedNumberOfStandardUsers: boolean = false;
  public exceedNumberOfAccountantUsers: boolean = false;
  public enforceTwoFactorAuthentication: boolean = false;
  public userTwoFactorEnabled: boolean = false;
  public terminals: any[] = [];
}
